/**
 * Cloudflare Turnstile CAPTCHA Component
 * Free, privacy-friendly CAPTCHA alternative to Google reCAPTCHA
 * 
 * Production Configuration:
 * - Domain: justonepcrepair.pages.dev
 * - Site Key: 0x4AAAAAAB6-pwimiHDcHaRQ (Public - Safe in client code)
 * - Environment Variable: REACT_APP_TURNSTILE_SITE_KEY
 * 
 * Setup Instructions:
 * 1. Local Development:
 *    - Copy .env.local.example to .env.local
 *    - Add REACT_APP_TURNSTILE_SITE_KEY=0x4AAAAAAB6-pwimiHDcHaRQ
 * 
 * 2. Production Deployment (Cloudflare Pages):
 *    - Go to Settings → Environment variables
 *    - Add REACT_APP_TURNSTILE_SITE_KEY=0x4AAAAAAB6-pwimiHDcHaRQ
 * 
 * Security Notes:
 * - Site Key (Public): Safe to commit, exposed in client-side code
 * - Secret Key: NEVER commit, server-side only (not used in React app)
 * 
 * Author: MCGdev
 */

import React from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const TurnstileCaptcha = ({ onVerify, onError, onExpire, darkMode = true, className = '' }) => {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Auto-verify if no site key (prevents form from breaking)
  React.useEffect(() => {
    if (!siteKey && onVerify) {
      console.warn('[Turnstile] No site key configured, auto-verifying');
      onVerify('no-captcha-configured');
    }
  }, [siteKey, onVerify]);

  // If no site key is configured, show a message in development
  if (!siteKey && process.env.NODE_ENV === 'development') {
    return (
      <div className={`p-4 rounded-lg border-2 border-dashed ${darkMode ? 'border-amber-400/50 bg-amber-900/20' : 'border-amber-500 bg-amber-50'} ${className}`}>
        <p className={`text-sm text-center ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          ⚠️ Turnstile CAPTCHA not configured
        </p>
        <p className={`text-xs text-center mt-1 ${darkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
          Add REACT_APP_TURNSTILE_SITE_KEY to .env.local
        </p>
      </div>
    );
  }

  // If no site key in production, return null (auto-verify happens in useEffect)
  if (!siteKey) {
    return null;
  }

  return (
    <div className={`flex justify-center ${className}`}>
      <Turnstile
        siteKey={siteKey}
        onSuccess={(token) => {
          console.log('[Turnstile] Verification successful');
          if (onVerify) {
            onVerify(token);
          }
        }}
        onError={(error) => {
          console.error('[Turnstile] Verification error:', error);
          if (onError) {
            onError(error);
          }
        }}
        onExpire={() => {
          console.warn('[Turnstile] Token expired');
          if (onExpire) {
            onExpire();
          }
        }}
        theme={darkMode ? 'dark' : 'light'}
        size="normal"
        appearance="always"
        retry="auto"
        retryInterval={8000}
        refreshExpired="auto"
      />
    </div>
  );
};

export default TurnstileCaptcha;
